import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import "./JobCodePicker.css";

/**
 * Workforce — Job Code picker (TIME-006).
 *
 * Filterable combobox for selecting a single job-code slug from the
 * org-configured active list. Replaces:
 *   - the native <select> in TimeLogEditModal
 *   - the free-text <input> in MissingPunchSection
 *   - new pre-clock-in sheet on ClockInOutScreen
 *
 * Authoritative inputs:
 *   - docs/design/TIME-006-design.md (this story's visual spec — ZURI)
 *   - docs/backend/TIME-006-tech.md  (contract snapshot — SIMONE)
 *
 * Spec rationale (kept here as a quick reference, not a re-spec):
 *   - Combobox over native <select>: scales to 25+ codes; mobile UX better
 *     than the iOS wheel picker.
 *   - Pure / dumb component: host fetches via jobCodeAPI.list({ active: true })
 *     and passes a normalized `codes` array. Picker does not know about the
 *     network. (Improves testability — see __tests__/JobCodePicker.test.js.)
 *
 * Data shape expected for `codes`:
 *   [{ code: string, displayName?: string, active?: boolean, _id?: string }]
 *
 * The backend contract (TIME-006-tech.md §3.1) returns objects with `code`,
 * `displayName`, `active`, `_id`, etc. We render `displayName` (falling back
 * to `code` if missing) and write `code` upstream via onChange.
 */

const NO_OP = () => {};

const norm = (s) => (typeof s === "string" ? s.toLowerCase().trim() : "");

const filterCodes = (codes, query) => {
  const q = norm(query);
  if (!q) return codes;
  return codes.filter((c) => {
    const code = norm(c.code);
    const display = norm(c.displayName || c.code);
    return code.includes(q) || display.includes(q);
  });
};

const sortByDisplayName = (codes) =>
  [...codes].sort((a, b) => {
    const aLabel = (a.displayName || a.code || "").toString();
    const bLabel = (b.displayName || b.code || "").toString();
    return aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
  });

/**
 * Highlight the matched substring in the rendered display label.
 * Returns a JSX fragment with the matched portion bolded — bold-only is the
 * accessible highlight per design spec §2.1 (no color-only differentiation).
 */
const renderHighlightedLabel = (label, query) => {
  const q = norm(query);
  if (!q) return label;
  const lc = (label || "").toLowerCase();
  const idx = lc.indexOf(q);
  if (idx === -1) return label;
  const head = label.slice(0, idx);
  const match = label.slice(idx, idx + q.length);
  const tail = label.slice(idx + q.length);
  return (
    <>
      {head}
      <strong>{match}</strong>
      {tail}
    </>
  );
};

const JobCodePicker = ({
  value = null,
  onChange = NO_OP,
  codes = [],
  loading = false,
  error = null,
  placeholder = "(use my primary role)",
  disabled = false,
  required = false,
  inputId,
  testId = "job-code-picker",
  size = "md",
  emptyState = "disabled-pill",
  errorMessage,
  // Optional: an "inactive" code held by the row that we still want to render
  // alongside the active list (TimeLogEditModal stale-row behavior — design
  // spec §3.1 "edge case kept").
  staleCode = null,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listboxId = useId();
  const reactInputId = useId();
  const resolvedInputId = inputId || `jcp-input-${reactInputId}`;
  const helperId = `${resolvedInputId}-helper`;

  // Items rendered in the popover. Stale code (if not in active list) gets
  // an "(inactive)" suffix and is sorted to the top divider section.
  const items = useMemo(() => {
    const sorted = sortByDisplayName(codes || []);
    const filtered = filterCodes(sorted, query);
    const out = filtered.map((c) => ({
      code: c.code,
      label: c.displayName || c.code,
      inactive: false,
    }));
    if (
      staleCode &&
      !sorted.some((c) => c.code === staleCode) &&
      norm(staleCode).includes(norm(query))
    ) {
      out.unshift({
        code: staleCode,
        label: `${staleCode} (inactive)`,
        inactive: true,
      });
    }
    return out;
  }, [codes, query, staleCode]);

  // Currently selected item (used for the closed-state input value rendering).
  const selectedItem = useMemo(() => {
    if (!value) return null;
    const fromActive = (codes || []).find((c) => c.code === value);
    if (fromActive) {
      return {
        code: fromActive.code,
        label: fromActive.displayName || fromActive.code,
        inactive: false,
      };
    }
    if (staleCode && staleCode === value) {
      return { code: staleCode, label: staleCode, inactive: true };
    }
    return { code: value, label: value, inactive: true };
  }, [value, codes, staleCode]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Reset active highlight when items list shape changes.
  useEffect(() => {
    if (activeIdx >= items.length) {
      setActiveIdx(items.length > 0 ? 0 : -1);
    }
  }, [items, activeIdx]);

  const isEmptyConfigured = !loading && !error && (codes || []).length === 0;
  const skipRender = isEmptyConfigured && emptyState === "skip" && !staleCode;

  const handleInputChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);
    setActiveIdx(0);
  };

  const handleFocus = () => {
    if (disabled) return;
    if (!open) setOpen(true);
  };

  const selectItem = useCallback(
    (item) => {
      onChange(item ? item.code : null);
      setOpen(false);
      setQuery("");
    },
    [onChange]
  );

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery("");
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setActiveIdx(0);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (items.length === 0) return;
      setActiveIdx((idx) => (idx + 1) % items.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length === 0) return;
      setActiveIdx((idx) => (idx - 1 + items.length) % items.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (items.length === 0) return;
      selectItem(items[activeIdx] || items[0]);
      return;
    }
    if (e.key === "Tab") {
      // Close popover but allow native tab to next field.
      setOpen(false);
    }
  };

  if (skipRender) {
    return null;
  }

  // The visible input text. When closed and a value is selected, show the
  // selected label. When open, show the live `query` (filtering input).
  const inputDisplayValue = open ? query : selectedItem ? selectedItem.label : query;
  const showClear = !required && !disabled && !!value && !open;

  const sizeClass = size === "sm" ? "jcp--sm" : "jcp--md";
  const stateClass = [
    open ? "jcp--open" : "",
    disabled ? "jcp--disabled" : "",
    loading ? "jcp--loading" : "",
    error || errorMessage ? "jcp--error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const placeholderText = loading
    ? "Loading job codes..."
    : isEmptyConfigured
    ? "(no job codes configured)"
    : placeholder;

  return (
    <div
      ref={containerRef}
      className={`jcp ${sizeClass} ${stateClass}`.trim()}
      data-testid={testId}
    >
      <div className="jcp__inputwrap">
        <input
          ref={inputRef}
          id={resolvedInputId}
          type="text"
          className="jcp__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && items[activeIdx]
              ? `${listboxId}-opt-${activeIdx}`
              : undefined
          }
          aria-required={required || undefined}
          aria-describedby={errorMessage ? helperId : undefined}
          aria-invalid={errorMessage ? true : undefined}
          autoComplete="off"
          disabled={disabled || loading || (isEmptyConfigured && !staleCode)}
          placeholder={placeholderText}
          value={inputDisplayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          data-testid={`${testId}-input`}
        />
        {loading ? (
          <span className="jcp__spinner" aria-hidden="true" />
        ) : (
          <>
            {showClear && (
              <button
                type="button"
                className="jcp__clear"
                aria-label="Clear job code"
                onClick={handleClear}
                tabIndex={-1}
                data-testid={`${testId}-clear`}
              >
                {/* multiplication sign — keeps ASCII-safe */}
                {"×"}
              </button>
            )}
            <span
              className={`jcp__chevron ${open ? "jcp__chevron--open" : ""}`}
              aria-hidden="true"
            />
          </>
        )}
      </div>
      {open && !disabled && !loading && (
        <ul
          className="jcp__listbox"
          id={listboxId}
          role="listbox"
          data-testid={`${testId}-listbox`}
        >
          {isEmptyConfigured && !staleCode ? (
            <li
              className="jcp__option jcp__option--empty"
              role="option"
              aria-disabled="true"
              aria-selected="false"
              data-testid={`${testId}-option-empty-config`}
            >
              No job codes configured.
            </li>
          ) : items.length === 0 ? (
            <li
              className="jcp__option jcp__option--empty"
              role="option"
              aria-disabled="true"
              aria-selected="false"
              data-testid={`${testId}-option-no-match`}
            >
              No matches for &ldquo;{query}&rdquo;
            </li>
          ) : (
            items.map((item, idx) => {
              const selected = value === item.code;
              const focused = idx === activeIdx;
              return (
                <li
                  key={`${item.code}-${item.inactive ? "inactive" : "active"}`}
                  id={`${listboxId}-opt-${idx}`}
                  role="option"
                  aria-selected={selected}
                  className={`jcp__option ${
                    focused ? "jcp__option--focused" : ""
                  } ${selected ? "jcp__option--selected" : ""} ${
                    item.inactive ? "jcp__option--inactive" : ""
                  }`}
                  onMouseEnter={() => setActiveIdx(idx)}
                  // onMouseDown so the click fires before input blur closes the popover.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectItem(item);
                  }}
                  data-testid={`${testId}-option-${item.code}`}
                >
                  {item.inactive ? (
                    <span>
                      {item.code}{" "}
                      <em className="jcp__inactive-suffix">(inactive)</em>
                    </span>
                  ) : (
                    renderHighlightedLabel(item.label, query)
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
      {(errorMessage || (isEmptyConfigured && emptyState === "disabled-pill")) && (
        <p
          id={helperId}
          className={`jcp__helper ${
            errorMessage ? "jcp__helper--error" : ""
          }`}
          role={errorMessage ? "alert" : undefined}
          aria-live={errorMessage ? "polite" : undefined}
          data-testid={`${testId}-helper`}
        >
          {errorMessage ||
            "An admin can add codes in Workforce > Settings > Job Codes."}
        </p>
      )}
      {error && !errorMessage && (
        <p
          className="jcp__helper jcp__helper--error"
          role="alert"
          data-testid={`${testId}-fetch-error`}
        >
          Couldn&apos;t load job codes. Refresh and try again.
        </p>
      )}
    </div>
  );
};

export default JobCodePicker;
